/**
 * Last-resort boot failure screen.
 *
 * ⚠ **Why this is hand-written DOM and not a React component.** Everything here
 * runs on the path where React has not rendered and may not be mountable — a
 * throw inside `new PublicClientApplication` happens at module scope, before any
 * root exists. A React error boundary cannot catch that; it is not mounted yet.
 * So this deliberately uses no framework, no theme, no i18n, and no imports it
 * could be brought down by.
 *
 * The rule it enforces: **an auth library must never be able to blank the entire
 * app silently.** On 2026-07-31 exactly that happened — MSAL was constructed with
 * `clientId: undefined`, threw, and the only evidence anywhere was a console
 * trace the user had to open dev-tools to find. A white page tells a bookkeeper
 * nothing and tells whoever they report it to even less.
 */

const CONTAINER_ID = 'root';

/** Keeps a stack out of the UI but reachable — the message is for the user, the stack for us. */
function describe(error: unknown): { message: string; detail: string } {
  if (error instanceof Error) {
    return { message: error.message, detail: error.stack ?? error.message };
  }
  return { message: String(error), detail: String(error) };
}

export function renderBootError(error: unknown): void {
  const { message, detail } = describe(error);

  // Log first and unconditionally: if the DOM write below throws for any reason,
  // the trace still reaches the console.
  console.error('[ptc-conversions] Application failed to start', error);

  const container = document.getElementById(CONTAINER_ID) ?? document.body;

  const wrapper = document.createElement('div');
  wrapper.setAttribute('role', 'alert');
  wrapper.style.cssText = [
    // The boot screen paints before Fluent loads, so it cannot use a token: the fleet stacks, verbatim (typeface guard).
    "font-family: 'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif",
    'max-width: 40rem',
    'margin: 4rem auto',
    'padding: 0 1.5rem',
    'color: #242424',
    'line-height: 1.5',
  ].join(';');

  const heading = document.createElement('h1');
  heading.textContent = 'PTC Conversions could not start';
  heading.style.cssText = 'font-size: 1.5rem; margin: 0 0 0.75rem';

  const explain = document.createElement('p');
  explain.textContent =
    'This is a configuration or sign-in problem in the app itself, not something you did. ' +
    'Nothing you were working on has been lost. Please send the details below to the PTC ' +
    'development team.';
  explain.style.cssText = 'margin: 0 0 1rem';

  const summary = document.createElement('p');
  summary.textContent = message;
  summary.style.cssText = [
    'margin: 0 0 1rem',
    'padding: 0.75rem 1rem',
    'background: #fdf3f4',
    'border-left: 4px solid #b10e1c',
    "font-family: Consolas, 'Courier New', Courier, monospace",
    'font-size: 0.875rem',
    'word-break: break-word',
  ].join(';');

  const details = document.createElement('details');
  const summaryToggle = document.createElement('summary');
  summaryToggle.textContent = 'Technical detail';
  summaryToggle.style.cssText = 'cursor: pointer; margin-bottom: 0.5rem';
  const pre = document.createElement('pre');
  pre.textContent = detail;
  pre.style.cssText =
    'white-space: pre-wrap; word-break: break-word; font-size: 0.75rem; color: #616161; margin: 0';
  details.append(summaryToggle, pre);

  wrapper.append(heading, explain, summary, details);

  // Replace rather than append: a half-rendered app plus an error is more
  // confusing than the error alone.
  container.replaceChildren(wrapper);
}
